import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { apiFetch } from '../api';
import { useAuth } from '../context/AuthContext';
import './SuperuserAgentChatLog.css';

// --- config ---
const DEFAULT_USER_ID = 2;
const DEFAULT_LIMIT = 300;
// --- end config ---

const KIND_LABELS = {
  graph: 'Graph',
  llm: 'LLM',
  tool: 'Tool',
  chain: 'Chain',
  tool_err: 'Tool error',
  chain_err: 'Chain error',
  llm_err: 'LLM error',
};

function formatTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'medium' });
  } catch {
    return iso;
  }
}

function formatJson(value) {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function stepTitle(step, index) {
  const kind = KIND_LABELS[step.k] || step.k || 'step';
  const name = step.n || `#${index + 1}`;
  const ms = step.ms != null ? ` · ${step.ms} ms` : '';
  return `${kind}: ${name}${ms}`;
}

function groupIntoThreads(messages) {
  const byChat = new Map();
  for (const msg of messages) {
    if (!byChat.has(msg.chat_id)) byChat.set(msg.chat_id, []);
    byChat.get(msg.chat_id).push(msg);
  }

  const threads = [];
  for (const [chatId, msgs] of byChat.entries()) {
    const turns = [];
    let current = null;
    for (const msg of msgs) {
      if (msg.role === 'user') {
        current = { user: msg, assistants: [] };
        turns.push(current);
      } else if (msg.role === 'assistant') {
        if (!current) {
          current = { user: null, assistants: [] };
          turns.push(current);
        }
        current.assistants.push(msg);
      }
    }
    threads.push({ chatId, turns });
  }
  return threads.sort((a, b) => Number(a.chatId) - Number(b.chatId));
}

function ToggleHeader({ open, onToggle, className, children }) {
  return (
    <button
      type="button"
      className={`AgentChatLog-toggle ${className || ''}`}
      onClick={onToggle}
      aria-expanded={open}
    >
      <span className="AgentChatLog-toggleIcon">{open ? '▼' : '▶'}</span>
      {children}
    </button>
  );
}

function TraceStep({ step, index }) {
  const [open, setOpen] = useState(false);
  const [openIn, setOpenIn] = useState(false);
  const [openOut, setOpenOut] = useState(false);
  const hasIn = step.i !== undefined && step.i !== null;
  const hasOut = step.o !== undefined && step.o !== null;

  return (
    <div className={`AgentChatLog-traceStep ${open ? 'AgentChatLog-traceStep--open' : ''}`}>
      <ToggleHeader
        open={open}
        onToggle={() => setOpen((v) => !v)}
        className="AgentChatLog-traceStepHeader"
      >
        <span className="AgentChatLog-traceStepTitle">{stepTitle(step, index)}</span>
        {step.t && <span className="AgentChatLog-traceStepTime">{step.t}</span>}
      </ToggleHeader>
      {open && (
        <div className="AgentChatLog-traceStepBody">
          {hasIn && (
            <div className="AgentChatLog-ioBlock">
              <ToggleHeader
                open={openIn}
                onToggle={() => setOpenIn((v) => !v)}
                className="AgentChatLog-ioToggle AgentChatLog-ioToggle--in"
              >
                Вход
              </ToggleHeader>
              {openIn && <pre className="AgentChatLog-ioPre">{formatJson(step.i)}</pre>}
            </div>
          )}
          {hasOut && (
            <div className="AgentChatLog-ioBlock">
              <ToggleHeader
                open={openOut}
                onToggle={() => setOpenOut((v) => !v)}
                className="AgentChatLog-ioToggle AgentChatLog-ioToggle--out"
              >
                Выход
              </ToggleHeader>
              {openOut && <pre className="AgentChatLog-ioPre">{formatJson(step.o)}</pre>}
            </div>
          )}
          {!hasIn && !hasOut && <div className="AgentChatLog-emptyStep">Нет входа/выхода.</div>}
        </div>
      )}
    </div>
  );
}

function AssistantBlock({ message }) {
  const [open, setOpen] = useState(false);
  const [openText, setOpenText] = useState(true);
  const [openTrace, setOpenTrace] = useState(false);
  const trace = Array.isArray(message.agent_run_log) ? message.agent_run_log : [];
  const meta = [
    `#${message.id}`,
    message.source || 'assistant',
    message.included_in_history === false ? 'не в истории' : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className={`AgentChatLog-assistant ${open ? 'AgentChatLog-assistant--open' : ''}`}>
      <ToggleHeader
        open={open}
        onToggle={() => setOpen((v) => !v)}
        className="AgentChatLog-assistantHeader"
      >
        <span className="AgentChatLog-assistantLabel">Ассистент</span>
        <span className="AgentChatLog-assistantMeta">{meta}</span>
        <span className="AgentChatLog-assistantTime">{formatTime(message.created_at)}</span>
        {trace.length > 0 && (
          <span className="AgentChatLog-badge">{trace.length} шагов</span>
        )}
      </ToggleHeader>
      {open && (
        <div className="AgentChatLog-assistantBody">
          <div className="AgentChatLog-textBlock">
            <ToggleHeader
              open={openText}
              onToggle={() => setOpenText((v) => !v)}
              className="AgentChatLog-textToggle"
            >
              Текст ответа
            </ToggleHeader>
            {openText && <pre className="AgentChatLog-textPre">{message.text || '—'}</pre>}
          </div>
          {trace.length > 0 && (
            <div className="AgentChatLog-traceBlock">
              <ToggleHeader
                open={openTrace}
                onToggle={() => setOpenTrace((v) => !v)}
                className="AgentChatLog-traceToggle"
              >
                Узлы агента ({trace.length})
              </ToggleHeader>
              {openTrace && (
                <div className="AgentChatLog-traceList">
                  {trace.map((step, i) => (
                    <TraceStep key={`${message.id}-step-${i}`} step={step} index={i} />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function UserBlock({ message }) {
  const [open, setOpen] = useState(false);
  const attachments = Array.isArray(message.attachments) ? message.attachments : [];

  return (
    <div className={`AgentChatLog-user ${open ? 'AgentChatLog-user--open' : ''}`}>
      <ToggleHeader open={open} onToggle={() => setOpen((v) => !v)} className="AgentChatLog-userHeader">
        <span className="AgentChatLog-userLabel">Пользователь</span>
        <span className="AgentChatLog-userMeta">#{message.id}</span>
        <span className="AgentChatLog-userTime">{formatTime(message.created_at)}</span>
      </ToggleHeader>
      {open && (
        <div className="AgentChatLog-userBody">
          <pre className="AgentChatLog-textPre">{message.text || '—'}</pre>
          {attachments.length > 0 && (
            <ul className="AgentChatLog-attachments">
              {attachments.map((att, i) => (
                <li key={`${message.id}-att-${i}`}>
                  <span className="AgentChatLog-attachmentType">{att.attachment_type}</span>
                  <a href={att.url} target="_blank" rel="noreferrer">
                    {att.url}
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function TurnCard({ turn, turnIndex }) {
  const [open, setOpen] = useState(false);
  const preview = turn.user?.text?.slice(0, 80) || turn.assistants[0]?.text?.slice(0, 80) || '—';

  return (
    <article className={`AgentChatLog-turn ${open ? 'AgentChatLog-turn--open' : ''}`}>
      <ToggleHeader open={open} onToggle={() => setOpen((v) => !v)} className="AgentChatLog-turnHeader">
        <span className="AgentChatLog-turnIndex">Сообщение {turnIndex + 1}</span>
        <span className="AgentChatLog-turnPreview">{preview}</span>
      </ToggleHeader>
      {open && (
        <div className="AgentChatLog-turnBody">
          {turn.user && <UserBlock message={turn.user} />}
          {turn.assistants.map((msg) => (
            <AssistantBlock key={msg.id} message={msg} />
          ))}
        </div>
      )}
    </article>
  );
}

function ThreadCard({ thread }) {
  const [open, setOpen] = useState(true);
  const threadLabel = `tg_${thread.chatId}`;

  return (
    <section className={`AgentChatLog-thread ${open ? 'AgentChatLog-thread--open' : ''}`}>
      <ToggleHeader open={open} onToggle={() => setOpen((v) => !v)} className="AgentChatLog-threadHeader">
        <span className="AgentChatLog-threadTitle">Тред {threadLabel}</span>
        <span className="AgentChatLog-threadMeta">{thread.turns.length} диалогов</span>
      </ToggleHeader>
      {open && (
        <div className="AgentChatLog-threadBody">
          {thread.turns.map((turn, i) => (
            <TurnCard key={`turn-${thread.chatId}-${i}`} turn={turn} turnIndex={i} />
          ))}
        </div>
      )}
    </section>
  );
}

export default function SuperuserAgentChatLog({ active }) {
  const { actor } = useAuth();
  const [userId, setUserId] = useState(DEFAULT_USER_ID);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!actor?.is_superuser) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        user_id: String(userId),
        limit: String(limit),
      });
      const res = await apiFetch(`/api/admin/agent-chat-logs/?${params}`);
      if (res.status === 403) {
        setError('Нет доступа (нужен суперпользователь).');
        setPayload(null);
        return;
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Ошибка ${res.status}`);
      }
      const json = await res.json();
      setPayload(json);
    } catch (e) {
      setError(e.message || 'Ошибка загрузки');
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [actor?.is_superuser, userId, limit]);

  useEffect(() => {
    if (active && actor?.is_superuser) {
      load();
    }
  }, [active, actor?.is_superuser, load]);

  const threads = useMemo(
    () => groupIntoThreads(Array.isArray(payload?.messages) ? payload.messages : []),
    [payload]
  );

  if (!actor?.is_superuser) {
    return <div className="AgentChatLog AgentChatLog-forbidden">Только для суперпользователя.</div>;
  }

  return (
    <div className="AgentChatLog">
      <div className="AgentChatLog-toolbar">
        <label>
          <span>user_id</span>
          <input
            type="number"
            className="AgentChatLog-input"
            value={userId}
            onChange={(e) => setUserId(Number(e.target.value) || DEFAULT_USER_ID)}
            min={1}
          />
        </label>
        <label>
          <span>Лимит</span>
          <select
            className="AgentChatLog-select"
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
          >
            <option value={100}>100</option>
            <option value={200}>200</option>
            <option value={300}>300</option>
            <option value={500}>500</option>
            <option value={1000}>1000</option>
          </select>
        </label>
        <button type="button" className="AgentChatLog-btn" onClick={load} disabled={loading}>
          Обновить
        </button>
        {payload && (
          <span className="AgentChatLog-summary">
            {payload.username} (id={payload.user_id}) · {payload.messages?.length ?? 0} сообщений
          </span>
        )}
      </div>

      {error && <div className="AgentChatLog-error">{error}</div>}
      {loading && <div className="AgentChatLog-loading">Загрузка…</div>}

      {!loading && !error && (
        <div className="AgentChatLog-list">
          {threads.length === 0 && (
            <div className="AgentChatLog-empty">Нет сообщений для user_id={userId}.</div>
          )}
          {threads.map((thread) => (
            <ThreadCard key={`thread-${thread.chatId}`} thread={thread} />
          ))}
        </div>
      )}
    </div>
  );
}
